import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { purchOrderService } from "../services/svc_purchaseorders";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest, ListRequestSchemaV2 } from "../types/common/commonTypes";
import { OrderStatusUpdate } from "../types/combinedTypes";
import { POReceiptResponse } from "../types/purchOrderTypes";

export const useTSQPurchOrderList = (params: ListRequest) => {
  return useQuery({
    queryKey: queryKeys.purchaseorder.list(params),
    queryFn: () => purchOrderService.list(params),
    placeholderData: keepPreviousData,
  });
};

export const useTSQPurchOrdersByVendor = (
  params: ListRequestSchemaV2,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.purchaseorder.listv2(params),
    queryFn: () => purchOrderService.listByVendor(params),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQPurchOrder = (
  id: string | null,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.purchaseorder.detail(id!),
    queryFn: () => purchOrderService.getById(id!),
    enabled: !!id && enabled,
  });
};

export const useTSMCreatePurchOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: purchOrderService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to create PO:", error);
    },
  });
};

export const useTSMPostProcessPurchOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => purchOrderService.postProcess(id),
    onSuccess: () => {},
    onError: (error) => {
      console.error("Failed to preform PO Post Processing:", error);
    },
  });
};

export const useTSMRegenPODocs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => purchOrderService.regenDoc(id),
    onSuccess: () => {},
    onError: (error) => {
      console.error("Failed to regen PO Document:", error);
    },
  });
};

export const useTSMReceivePurchOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      purchOrderService.receive(id, data),
    onSuccess: (receivedPurchaseOrder) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.detail(receivedPurchaseOrder.id),
      });
    },
    onError: (error) => {
      console.error("Failed to receive PO:", error);
    },
  });
};

export const useTSMGenerateRecDocs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: POReceiptResponse }) =>
      purchOrderService.generateRecDocs(data),
    onSuccess: () => {},
    onError: (error) => {
      console.error("Failed to generate receiving documents:", error);
    },
  });
};

export const useTSMUpdatePurchOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      purchOrderService.update(id, data),
    onSuccess: (updatePurchaseOrder) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.detail(updatePurchaseOrder.id),
      });
    },
    onError: (error) => {
      console.error("Failed to update PO:", error);
    },
  });
};

export const useTSMUpdatePurchOrderApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      purchOrderService.updateApproval(id, data),
    onSuccess: (updatePurchaseOrder) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.detail(updatePurchaseOrder.id),
      });
    },
    onError: (error) => {
      console.error("Failed to update PO Approval:", error);
    },
  });
};

export const useTSMStatusUpdatePO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: OrderStatusUpdate }) =>
      purchOrderService.statusUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.all,
      });
    },
    onError: (error) => {
      console.error("Failed to Update PO Status:", error);
    },
  });
};

export const useTSMCompletePO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: purchOrderService.markComplete,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to mark PO Complete:", error);
    },
  });
};

export const useTSMVoidPO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: purchOrderService.markVoid,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to mark PO Void:", error);
    },
  });
};

export const useTSMDeactivatePO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: purchOrderService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to deactivate PO:", error);
    },
  });
};

export const useTSMReactivatePO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: purchOrderService.reactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.purchaseorder.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to reactivate PO:", error);
    },
  });
};
